// Copyright (c) StrangeBee 2024

import { ChangeEvent, ReactElement, useEffect, useRef, useState, KeyboardEvent, memo } from 'react';
import { SendOutlined } from '@ant-design/icons';
import { classList } from '@Helpers/className';
import { useLocalMessage } from '@Helpers/locale';

import { THForm } from '@Molecules/THForm/THForm';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { THTextArea } from '@Molecules/Inputs/THTextArea/THTextArea';

import './CommentForm.scss';

interface CommentFormProps {
    footerText?: string;
    placeholder?: string;
    onSubmit: (text: string) => void;
    onReset?: () => void;
    message?: string;
    isInComment?: boolean;
}

export const CommentForm = memo(
    ({
        footerText,
        placeholder,
        onSubmit,
        onReset,
        message = '',
        isInComment = false,
    }: CommentFormProps): ReactElement => {
        const [content, setContent] = useState(message);
        const [isValid, setIsValid] = useState<boolean>(false);
        const refGrowDiv = useRef<HTMLDivElement>(null);
        const i = useLocalMessage();

        useEffect(() => {
            if (refGrowDiv.current) {
                refGrowDiv.current.dataset.replicatedValue = content;
            }
        }, [content]);

        const onTextChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
            setContent(e.target.value);
        };

        const handleSubmit = (): void => {
            onSubmit?.(content);
            setContent('');
        };

        const onPressEnter = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
            if (!isValid) {
                e.preventDefault();
                return;
            }

            if (!e.shiftKey) {
                e.preventDefault();
                handleSubmit();
            }
        };

        useEffect(() => {
            setIsValid(content.trim() !== '');
        }, [content]);

        return (
            <div className={classList('CommentForm__Wrapper', { 'CommentForm__Wrapper--contained': isInComment })}>
                <THForm className="CommentForm" onFinish={handleSubmit}>
                    <div ref={refGrowDiv} className="CommentForm__VirtualInput">
                        <THTextArea
                            onChange={onTextChange}
                            className={classList('CommentForm__Input', { 'CommentForm__Input--isEmpty': !content })}
                            placeholder={placeholder}
                            value={content}
                            onPressEnter={onPressEnter}
                            rows={1}
                        />
                    </div>

                    {(isInComment && (
                        <footer className="CommentForm__Actions">
                            {!!onReset && (
                                <THButton danger type="link" color="red" htmlType="reset" onClick={onReset}>
                                    {i('comment.cancel')}
                                </THButton>
                            )}
                            <THButton type="primary" htmlType="submit" disabled={!isValid}>
                                {i('comment.submit')}
                            </THButton>
                        </footer>
                    )) || (
                        <THButton htmlType="submit" className="CommentForm__Submit" disabled={!isValid}>
                            <SendOutlined />
                        </THButton>
                    )}
                </THForm>

                {!!footerText && !isInComment && <footer className="CommentForm__Footer">{footerText}</footer>}
            </div>
        );
    },
);
